const { buildAlgorithmInput } = require('./build-algorithm-input')
const { scoreAndSortInputs } = require('./urgency-score')
const { getThresholds } = require('../../../forecast/tools/read/psm-thresholds')
const { selectCostEffectiveSku } = require('./select-cost-effective-sku')

/**
 * Single entry point for auto-fill quantity calculation.
 * Branches on whether budgetAmount is provided:
 *   - No budget → basic auto-fill: fill all products below effectiveMax to max
 *   - With budget → two-pass fair-share allocation
 *
 * @param {object} params
 * @param {object} params.warehouseStock - genericGroups with SKU pricing/packs
 * @param {Object<string, number>} params.ledger - productId → available packs
 * @param {object|null} params.qtoData - consumption history
 * @param {string} params.programId - for stock thresholds
 * @param {number} params.monthsInPeriod - reporting cycle length in months
 * @param {number} [params.budgetAmount] - omit for basic auto-fill
 * @returns {Array<{ productId: string, genericId: string|number, packsToOrder: number, costAllocated: number }>}
 */
function calculateAutoFillQuantities ({
  warehouseStock,
  ledger,
  qtoData,
  programId,
  monthsInPeriod,
  budgetAmount
}) {
  if (budgetAmount != null && isFinite(budgetAmount) && budgetAmount > 0) {
    return runBudgetOptimization({ warehouseStock, ledger, qtoData, programId, monthsInPeriod, budgetAmount })
  }
  return runBasicFill({ warehouseStock, ledger, qtoData, programId, monthsInPeriod })
}

// ─── Basic fill (no budget constraint) ───────────────────────────────────────
// Triggered by: user clicks Skip in budget modal, missing min/max config fallback.

function runBasicFill ({ warehouseStock, ledger, qtoData, programId, monthsInPeriod }) {
  const thresholds = getThresholds(programId)
  if (!thresholds) return []

  const { min, reOrder, max } = thresholds
  const leadTimeMonths = reOrder.months - min.months
  const { genericGroups = [] } = warehouseStock
  const qtoProducts = (qtoData && qtoData.products) || {}
  const results = []

  for (const group of genericGroups) {
    const productsWithAvailability = group.products.map(p => {
      const availablePacks = ledger ? ledger[p.productId] : undefined
      return {
        ...p,
        warehouseAvailablePacks: (availablePacks != null && isFinite(availablePacks))
          ? availablePacks
          : Infinity
      }
    })

    const inStock = productsWithAvailability.filter(p => p.warehouseAvailablePacks > 0)
    const candidatePool = inStock.length > 0 ? inStock : productsWithAvailability

    const bestSku = selectCostEffectiveSku(candidatePool)
    if (!bestSku) continue

    const qto = qtoProducts[group.genericId]
    if (!qto || qto.consumed == null) continue

    const amc = qto.consumed / monthsInPeriod
    const soh = qto.soh != null ? qto.soh : 0
    const effectiveMaxTarget = amc * (max.months + leadTimeMonths)
    const unitsNeeded = Math.max(0, effectiveMaxTarget - soh)
    if (unitsNeeded <= 0) continue

    const packsToOrder = Math.ceil(unitsNeeded / bestSku.genericFactor)
    const warehouseCap = isFinite(bestSku.warehouseAvailablePacks)
      ? bestSku.warehouseAvailablePacks
      : packsToOrder
    const finalPacks = Math.min(packsToOrder, warehouseCap)
    if (finalPacks <= 0) continue

    results.push({
      productId: bestSku.productId,
      genericId: group.genericId,
      packsToOrder: finalPacks,
      costAllocated: finalPacks * bestSku.sellPrice
    })
  }

  return results
}

// ─── Budget optimization (Phase 2) ───────────────────────────────────────────
// Two-pass fair-share allocation.

function runBudgetOptimization ({ warehouseStock, ledger, qtoData, programId, monthsInPeriod, budgetAmount }) {
  const { inputs, avgAmc, maxCostPerUnit } = buildAlgorithmInput({
    warehouseStock, ledger, qtoData, programId, monthsInPeriod
  })
  if (inputs.length === 0) return []

  const items = scoreAndSortInputs(inputs, { avgAmc, maxCostPerUnit })
  let remainingBudget = budgetAmount

  // Pass 1: fill to min target in urgency order
  for (const item of items) {
    if (remainingBudget <= 0) break
    if (!isFinite(item.warehouseAvailablePacks)) continue

    const deficitUnits = Math.max(0, item.effectiveMinTarget - item.soh)
    if (deficitUnits <= 0) continue

    const packsNeeded = Math.ceil(deficitUnits / item.genericFactor)
    const affordablePacks = Math.floor(remainingBudget / item.sellPrice)
    const warehouseCap = isFinite(item.warehouseAvailablePacks)
      ? item.warehouseAvailablePacks
      : packsNeeded
    const packsToAllocate = Math.min(packsNeeded, affordablePacks, warehouseCap)
    if (packsToAllocate <= 0) continue

    item.genericUnitsAllocated += packsToAllocate * item.genericFactor
    remainingBudget -= packsToAllocate * item.sellPrice

    // PRD: if budget can't cover full gap, allocate what we can and STOP Pass 1
    if (packsToAllocate < packsNeeded) {
      remainingBudget = 0
      break
    }
  }

  // Pass 2: water-fill remaining budget to max (round-based equal-share)
  const getHeadroomPacks = (item) => {
    const headroomUnits = Math.max(0, item.effectiveMaxTarget - item.soh - item.genericUnitsAllocated)

    // ceil matches runBasicFill: a fractional-pack gap still warrants one more pack (slight max overshoot is acceptable)
    const headroomFromTarget = Math.ceil(headroomUnits / item.genericFactor)

    if (!isFinite(item.warehouseAvailablePacks)) return headroomFromTarget
    const packsAllocatedSoFar = item.genericUnitsAllocated / item.genericFactor
    const remainingWarehousePacks = Math.max(0, item.warehouseAvailablePacks - packsAllocatedSoFar)
    return Math.min(headroomFromTarget, Math.floor(remainingWarehousePacks))
  }

  let eligible = items.filter(item => getHeadroomPacks(item) > 0)

  while (eligible.length > 0) {
    const minPackCost = Math.min(...eligible.map(i => i.sellPrice))
    if (remainingBudget < minPackCost) break

    const equalShareBudget = remainingBudget / eligible.length
    const saturated = new Set()
    let budgetConsumedThisRound = 0

    for (const item of eligible) {
      const headroomPacks = getHeadroomPacks(item)
      if (headroomPacks <= 0) {
        saturated.add(item)
        continue
      }
      const costOfFull = headroomPacks * item.sellPrice
      if (costOfFull <= equalShareBudget) {
        item.genericUnitsAllocated += headroomPacks * item.genericFactor
        budgetConsumedThisRound += costOfFull
        saturated.add(item)
      } else {
        const packShare = Math.floor(equalShareBudget / item.sellPrice)
        item.genericUnitsAllocated += packShare * item.genericFactor
        budgetConsumedThisRound += packShare * item.sellPrice
      }
    }

    remainingBudget -= budgetConsumedThisRound
    eligible = eligible.filter(item => !saturated.has(item) && getHeadroomPacks(item) > 0)
    if (saturated.size === 0 && budgetConsumedThisRound === 0) break
  }

  // Greedy fallback: when fair-share deadlocks due to pack granularity (expensive items
  // dilute the equal share below cheaper items' minimum pack cost), spend any remaining
  // budget in urgency order rather than leaving it unspent.
  for (const item of items) {
    if (remainingBudget <= 0) break
    if (!isFinite(item.warehouseAvailablePacks)) continue
    const headroomPacks = getHeadroomPacks(item)
    if (headroomPacks <= 0) continue
    const packsToAdd = Math.min(headroomPacks, Math.floor(remainingBudget / item.sellPrice))
    if (packsToAdd <= 0) continue
    item.genericUnitsAllocated += packsToAdd * item.genericFactor
    remainingBudget -= packsToAdd * item.sellPrice
  }

  return items
    .map(item => ({
      productId: item.productId,
      genericId: item.genericId,
      packsToOrder: item.genericUnitsAllocated / item.genericFactor,
      costAllocated: (item.genericUnitsAllocated / item.genericFactor) * item.sellPrice
    }))
    .filter(result => result.packsToOrder > 0)
}

exports.calculateAutoFillQuantities = calculateAutoFillQuantities
